import { useEffect, useRef, useState } from "react";
import { smbClientIntakeRestoredLinkSchema } from "@shared/smb-client-intake-admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Raw fetch deliberately avoids mutation/query caches and response-body errors.
export function ClientIntakeRestoreLinkDialog({ id, onClose, onChanged }: {
  id: string; onClose: () => void; onChanged: () => void;
}) {
  const alive = useRef(true), inFlight = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const [originalUrl, setOriginalUrl] = useState("");
  const [pending, setPending] = useState(false), [error, setError] = useState("");
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; controller.current?.abort(); };
  }, []);
  const close = () => {
    alive.current = false; controller.current?.abort(); setOriginalUrl(""); setError(""); onClose();
  };
  const restore = async () => {
    if (inFlight.current) return;
    inFlight.current = true; setPending(true); setError("");
    controller.current = new AbortController();
    try {
      const response = await fetch(`/api/admin/smb/client-intake/links/${encodeURIComponent(id)}/restore`, {
        method: "POST", credentials: "include", cache: "no-store", signal: controller.current.signal,
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ originalUrl }),
      });
      // Discard the pasted credential even on a failed/uncertain request.
      if (!alive.current) return;
      setOriginalUrl("");
      if (!response.ok) throw new Error();
      smbClientIntakeRestoredLinkSchema.parse(await response.json());
      if (!alive.current) return;
      onChanged(); close();
    } catch {
      if (alive.current) {
        setOriginalUrl("");
        setError("Restoration could not be confirmed. Check your access, the original URL for this entry, the configured public origin and encryption configuration. Refresh before retrying.");
      }
    } finally {
      inFlight.current = false;
      if (alive.current) setPending(false);
    }
  };
  return <Dialog open onOpenChange={open => { if (!open) close(); }}>
    <DialogContent className="w-[calc(100%-2rem)] max-w-lg" data-sentry-mask>
      <DialogTitle>Restore original link</DialogTitle>
      <DialogDescription>Paste an original personal intake URL you already have for this entry. Restoration keeps the original link, client sessions, expiry and revoked status unchanged. Nothing is sent and no replacement is created.</DialogDescription>
      <form className="min-w-0 space-y-3" onSubmit={event => { event.preventDefault(); void restore(); }}>
        <label className="block text-sm">Original personal intake URL
          <Input type="password" required autoComplete="off" spellCheck={false} autoCapitalize="none"
            maxLength={2048} value={originalUrl} disabled={pending}
            onChange={event => setOriginalUrl(event.target.value)} />
        </label>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={pending || !originalUrl}>{pending ? "Restoring..." : "Confirm restoration"}</Button>
      </form>
      <Button variant="outline" onClick={close}>Cancel</Button>
    </DialogContent>
  </Dialog>;
}
